import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { clearGoogleMapsConfigCache } from '../utils/googleMapsConfig';

interface SystemSetting {
  key: string;
  value: string | null;
  description: string;
  is_sensitive: boolean;
  updated_at: string;
  updated_by_email?: string;
  decrypted?: boolean;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [showSensitive, setShowSensitive] = useState<Record<string, boolean>>({});
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'email' | 'notifications'>('general');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/settings', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchDecryptedValue = async (key: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/settings/${key}?decrypt=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch setting');
      }

      const data = await response.json();
      return data.value;
    } catch (error) {
      console.error('Error fetching decrypted value:', error);
      toast.error('Failed to load setting value');
      return null;
    }
  };

  const handleEdit = async (key: string, setting: SystemSetting) => {
    setEditingKey(key);
    
    // If it's a sensitive setting, fetch the decrypted value
    if (setting.is_sensitive) {
      const decryptedValue = await fetchDecryptedValue(key);
      if (decryptedValue) {
        setEditValues({ ...editValues, [key]: decryptedValue });
      }
    } else {
      setEditValues({ ...editValues, [key]: setting.value || '' });
    }
  };

  const handleSave = async (key: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/settings/${key}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          value: editValues[key] || null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update setting');
      }

      toast.success('Setting updated successfully');
      setEditingKey(null);
      
      // Clear Google Maps cache if related setting was updated
      if (key.includes('google_maps')) {
        clearGoogleMapsConfigCache();
      }
      
      fetchSettings(); // Refresh the list
    } catch (error) {
      console.error('Error updating setting:', error);
      toast.error('Failed to update setting');
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValues({});
  };

  const toggleShowSensitive = (key: string) => {
    setShowSensitive(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }

    setSendingTestEmail(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/settings/test-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: testEmailAddress
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Test email sent successfully! Check your inbox.');
      } else {
        toast.error(data.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error('Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const getSettingIcon = (key: string) => {
    if (key.includes('api_key')) return '🔑';
    if (key.includes('smtp')) return '📧';
    if (key.includes('notification')) return '🔔';
    if (key.includes('enabled')) return '⚙️';
    if (key.includes('retention')) return '📅';
    if (key.includes('max')) return '📊';
    if (key.includes('password')) return '🔒';
    if (key.includes('email')) return '✉️';
    return '📝';
  };

  const formatSettingName = (key: string) => {
    return key
      .replace(/_/g, ' ')
      .replace(/smtp/g, 'SMTP')
      .replace(/api/g, 'API')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getInputType = (key: string) => {
    if (key.includes('password')) return 'password';
    if (key.includes('email')) return 'email';
    if (key.includes('port')) return 'number';
    if (key === 'smtp_secure' || key === 'smtp_enabled' || key.includes('notification_email')) {
      return 'select';
    }
    return 'text';
  };

  const filterSettingsByTab = (settings: SystemSetting[]) => {
    switch (activeTab) {
      case 'email':
        return settings.filter(s => s.key.startsWith('smtp_'));
      case 'notifications':
        return settings.filter(s => s.key.startsWith('notification_'));
      default:
        return settings.filter(s => !s.key.startsWith('smtp_') && !s.key.startsWith('notification_'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading settings...</div>
      </div>
    );
  }

  const filteredSettings = filterSettingsByTab(settings);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">System Settings</h2>
          <button
            onClick={fetchSettings}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('general')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'general'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              General Settings
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'email'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Email Configuration
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'notifications'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Notification Settings
            </button>
          </nav>
        </div>

        <div className="bg-gray-800 rounded-lg border border-gray-700">
          <div className="p-6 space-y-4">
            {filteredSettings.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No settings available in this category
              </div>
            ) : (
              filteredSettings.map((setting) => (
                <div
                  key={setting.key}
                  className="bg-gray-900 rounded-lg p-4 border border-gray-700"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{getSettingIcon(setting.key)}</span>
                        <h3 className="text-lg font-semibold text-white">
                          {formatSettingName(setting.key)}
                        </h3>
                        {setting.is_sensitive && (
                          <span className="px-2 py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded">
                            Sensitive
                          </span>
                        )}
                      </div>

                      {setting.description && (
                        <p className="text-gray-400 dark:text-gray-500 text-sm mb-3">
                          {setting.description}
                        </p>
                      )}

                      <div className="space-y-2">
                        {editingKey === setting.key ? (
                          <div className="flex gap-2">
                            {getInputType(setting.key) === 'select' ? (
                              <select
                                value={editValues[setting.key] || ''}
                                onChange={(e) => setEditValues({
                                  ...editValues,
                                  [setting.key]: e.target.value
                                })}
                                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
                              >
                                <option value="">Select...</option>
                                <option value="true">Yes/Enabled</option>
                                <option value="false">No/Disabled</option>
                              </select>
                            ) : (
                              <input
                                type={setting.is_sensitive && !showSensitive[setting.key] ? 'password' : getInputType(setting.key)}
                                value={editValues[setting.key] || ''}
                                onChange={(e) => setEditValues({
                                  ...editValues,
                                  [setting.key]: e.target.value
                                })}
                                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
                                placeholder={`Enter ${formatSettingName(setting.key).toLowerCase()}`}
                              />
                            )}
                            {setting.is_sensitive && getInputType(setting.key) !== 'select' && (
                              <button
                                onClick={() => toggleShowSensitive(setting.key)}
                                className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                                type="button"
                              >
                                {showSensitive[setting.key] ? '👁️' : '👁️‍🗨️'}
                              </button>
                            )}
                            <button
                              onClick={() => handleSave(setting.key)}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancel}
                              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="text-gray-300">
                              {setting.value ? (
                                setting.key === 'smtp_secure' || setting.key === 'smtp_enabled' || setting.key.includes('notification_email') ? 
                                  (setting.value === 'true' ? 'Enabled' : 'Disabled') : 
                                  setting.value
                              ) : (
                                <span className="text-gray-500 dark:text-gray-400 italic">Not configured</span>
                              )}
                            </div>
                            <button
                              onClick={() => handleEdit(setting.key, setting)}
                              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm transition-colors"
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </div>

                      {setting.updated_by_email && (
                        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                          Last updated by {setting.updated_by_email} on{' '}
                          {new Date(setting.updated_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Test Email Section - Only show on Email tab */}
        {activeTab === 'email' && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Test Email Configuration</h3>
            <p className="text-gray-400 dark:text-gray-500 text-sm mb-4">
              Send a test email to verify your SMTP settings are configured correctly.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                placeholder="Enter email address"
                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
              />
              <button
                onClick={handleTestEmail}
                disabled={sendingTestEmail}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded transition-colors"
              >
                {sendingTestEmail ? 'Sending...' : 'Send Test Email'}
              </button>
            </div>
          </div>
        )}

        <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-blue-400 text-xl">ℹ️</span>
            <div className="text-sm text-blue-300">
              <p className="font-semibold mb-1">
                {activeTab === 'email' ? 'Email Configuration' : 
                 activeTab === 'notifications' ? 'Notification Settings' : 
                 'About System Settings'}
              </p>
              <p>
                {activeTab === 'email' ? 
                  'Configure SMTP settings to enable email notifications for violations, reports, and account management.' :
                 activeTab === 'notifications' ?
                  'Control which notifications are sent and how they are delivered.' :
                  'These settings control system-wide features and integrations. Sensitive settings like API keys are encrypted in the database and masked in the interface for security.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;